/**
 * Key Expertise section – skills/headlines only, no names or photos
 */
import { motion } from 'motion/react';
import { Lightbulb, Code2, GraduationCap, Shield } from 'lucide-react';
import { content } from '@/landing/data/content';

const iconMap = {
  Lightbulb,
  Code2,
  GraduationCap,
  Shield,
};

export function Team() {
  return (
    <section id="team" className="relative py-24 lg:py-32 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Key Expertise
          </h2>
          <p className="text-xl text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            The skills and expertise that power our platform
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto items-stretch">
          {content.expertise.map((item, index) => {
            const Icon = iconMap[item.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -6 }}
                className="group insolip-card-gradient rounded-2xl border border-[rgba(255,255,255,0.08)] p-8 hover:border-[rgba(201,168,76,0.3)] transition-all duration-300 h-full flex flex-col"
              >
                <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/10 border border-[rgba(201,168,76,0.3)] text-[var(--gold)] mb-5 flex-shrink-0">
                  {Icon && <Icon className="w-7 h-7" />}
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3 group-hover:text-[var(--gold)] transition-colors">
                  {item.title}
                </h3>
                <p className="text-white/85 leading-relaxed text-sm font-light flex-1">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-16"
        >
          <div className="inline-block px-8 py-6 insolip-card-gradient rounded-2xl border border-[rgba(201,168,76,0.25)]">
            <h3 className="font-display text-2xl font-semibold text-white mb-2">
              Want to Join Us?
            </h3>
            <p className="text-white/85 mb-4 font-light">
              We're always looking for talented individuals to join our mission
            </p>
            <a
              href="#contact"
              className="inline-block px-6 py-3 bg-[var(--gold)] text-[var(--navy)] font-semibold rounded-full hover:opacity-90 hover:shadow-lg transition-all duration-300"
            >
              Get in Touch
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
